import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../../app/trpc.js";
import { Notice, SectionTitle } from "../../../ui/index.js";
import { DraftAnswers } from "../DraftAnswers.js";
import { digits, mergeRows, rowValid, type ServiceRow, ServiceRowsEditor, toDraft, toRow } from "../ServiceRows.js";
import { SourcesPanel } from "../SourcesPanel.js";
import { StepNav } from "../StepNav.js";
import { type AnswerForm, fromForm, MAX_SERVICES, type ParseDraft, toForm } from "../sources.js";
import type { StepProps } from "../types.js";
import { isDirty, useUnsavedChanges } from "../unsaved.js";

/**
 * Шаг 2: услуги и цены. Черновик из шаблона; владелец может дать свои источники: фото прайса (подойдёт и скриншот
 * поста Instagram), PDF, Word, Excel, голосовое, текст, и модель предложит услуги и ответы на вопросы о правилах.
 * Владелец проверяет и правит; ответы сохраняются в черновик и подставляются на шаге правил. Файлы не сохраняются.
 */
export function ServicesStep({ salonId, state, next, back }: StepProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ServiceRow[]>(() => (state.servicesDraft ?? []).map(toRow));
  const [answers, setAnswers] = useState<AnswerForm>(() => toForm(state.answersDraft ?? {}));
  // Черновик, с которым шаг открылся: пока правки не ушли на сервер («Сохранить услуги» или «Назад»), уход с шага спрашивает подтверждение.
  const [initial] = useState(() => ({ rows, answers }));
  useUnsavedChanges(isDirty(initial, { rows, answers }));
  const [added, setAdded] = useState<{ services: number; answers: number } | null>(null);
  const [dropped, setDropped] = useState(0);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const saveDraft = useMutation(trpc.onboarding.saveDraft.mutationOptions());
  const commit = useMutation(
    trpc.onboarding.commitServices.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.calendar.pathFilter());
        next({ saved: true });
      },
    }),
  );

  const answersDraft = fromForm(answers, state.questions);
  const valid = rows.length > 0 && rows.every(rowValid) && answersDraft !== null;
  // Черновик для следующего разбора: заполненные строки и проверяемые ответы (неверное число в форме ответы не отменяет).
  const parseDraft: ParseDraft = {
    services: rows.filter(rowValid).map((r) => ({ name: r.name.trim(), priceRsd: Number(digits(r.price)), durationMin: Number(digits(r.minutes)) })),
    answers: answersDraft ?? {},
  };

  return (
    <div>
      <p className="mb-4 text-ink-2">{t("onb.services.intro")}</p>

      <SectionTitle>{t("onb.sources.title")}</SectionTitle>
      {state.priceParsing ? (
        <SourcesPanel
          salonId={salonId}
          voiceInput={state.voiceInput}
          draft={parseDraft}
          onParsed={(r) => {
            // Строки и ответы берём из ref: разбор идёт секунды, владелец мог за это время поправить список.
            const merged = mergeRows(rowsRef.current, r.services);
            // Сервер принимает не больше 100 услуг: лишнее не добавляем и говорим об этом прямо.
            setRows(merged.slice(0, MAX_SERVICES));
            setDropped(Math.max(0, merged.length - MAX_SERVICES));
            // Модель возвращает ответы, объединённые с черновиком; чего она не вернула, в форме остаётся.
            const cur = answersRef.current;
            const returned = toForm(r.answers);
            setAnswers({ ...cur, ...returned });
            const changed = Object.entries(returned).filter(([id, v]) => JSON.stringify(cur[id]) !== JSON.stringify(v)).length;
            setAdded({ services: r.services.length, answers: changed });
          }}
        />
      ) : (
        <Notice>{t("onb.services.noModel")}</Notice>
      )}
      {dropped > 0 && <Notice kind="error">{t("onb.sources.tooManyServices", { count: dropped, max: MAX_SERVICES })}</Notice>}
      {added !== null && (
        <Notice kind="success">
          {t("onb.sources.added", { count: added.services })} {t("onb.sources.addedAnswers", { count: added.answers })}
        </Notice>
      )}

      <DraftAnswers
        questions={state.questions}
        form={answers}
        onChange={(id, v) => setAnswers({ ...answers, [id]: v })}
        onRemove={(id) => setAnswers(Object.fromEntries(Object.entries(answers).filter(([k]) => k !== id)))}
      />

      <SectionTitle>{t("onb.services.list")}</SectionTitle>
      <ServiceRowsEditor rows={rows} onChange={setRows} />
      {rows.length > 0 && !rows.every(rowValid) && <Notice kind="error">{t("onb.services.invalid")}</Notice>}
      {answersDraft === null && <Notice kind="error">{t("onb.sources.answersInvalid")}</Notice>}
      {commit.isError && <Notice kind="error">{t("common.error")}</Notice>}
      <p className="mt-4 text-sm text-ink-3">{t("onb.services.saveHint")}</p>
      <StepNav
        onBack={() => {
          // Заполненный черновик «Назад» сохраняет сам, подтверждение не нужно; с неверной строкой или числом в ответах
          // черновик не уйдёт, и мастер спросит.
          const saved = rows.every(rowValid) && answersDraft !== null;
          if (saved) saveDraft.mutate({ salonId, services: rows.map(toDraft), answersDraft });
          back({ saved });
        }}
        onNext={() => answersDraft && commit.mutate({ salonId, services: rows.map(toDraft), answersDraft })}
        nextLabel={t("onb.services.save")}
        disabled={!valid}
        busy={commit.isPending}
      />
    </div>
  );
}
